import { select } from "d3-selection";
import { scaleLinear } from "d3-scale";
import { max } from "d3-array";
import { Category } from "../../../databinding/Categorize";

export interface HistogramChartOptions {
    width?: number;
    height?: number;
    marginTop?: number;
    marginRight?: number;
    marginBottom?: number;
    marginLeft?: number;
    barColor?: string;
}

export class HistogramChart {
    private defaultOptions: HistogramChartOptions = {
        width: 800,
        height: 600,
        marginTop: 20,
        marginRight: 100,
        marginBottom: 50,
        marginLeft: 200,
        barColor: "#4CAF50"
    };

    constructor(private options: HistogramChartOptions = {}) {
        this.options = { ...this.defaultOptions, ...options };
    }

    render(container: HTMLElement, categories: Category[], formatTooltip: (cat: Category) => string): void {
        const { width, height, marginTop, marginRight, marginBottom, marginLeft, barColor } = this.options;
        const margin = { top: marginTop, right: marginRight, bottom: marginBottom, left: marginLeft };
        const barHeight = 25;
        const barPadding = 3;

        // Create SVG
        const svg = select(container)
            .append("svg")
            .attr("width", width)
            .attr("height", Math.max(height, categories.length * (barHeight + barPadding) + margin.top + margin.bottom))
            .style("font", "12px sans-serif")
            .style("background", "#fafafa");

        const g = svg.append("g")
            .attr("transform", `translate(${margin.left},${margin.top})`);

        // Create scales
        const maxValue = max(categories, (d: Category) => d.itemCount) || 0;
        const xScale = scaleLinear()
            .domain([0, maxValue])
            .range([0, width - margin.left - margin.right]);

        // Create bars
        const bars = g.selectAll("g")
            .data(categories)
            .join("g")
            .attr("transform", (d: Category, i: number) => `translate(0,${i * (barHeight + barPadding)})`);

        // Add rectangles
        bars.append("rect")
            .attr("x", 0)
            .attr("width", (d: Category) => xScale(d.itemCount))
            .attr("height", barHeight)
            .attr("fill", barColor)
            .attr("stroke", "#fff")
            .attr("stroke-width", 1)
            .style("fill-opacity", 0.8);

        // Add tooltips
        bars.append("title")
            .text((d: Category) => {
                let tooltip = formatTooltip(d);
                tooltip += `Items: ${d.itemCount}`;
                return tooltip;
            });

        // Add labels
        bars.append("text")
            .attr("x", -5)
            .attr("y", barHeight / 2)
            .attr("dy", "0.35em")
            .attr("text-anchor", "end")
            .style("fill", "#333")
            .style("font-size", "12px")
            .style("font-weight", "bold")
            .style("pointer-events", "none")
            .text((d: Category) => d.name);

        // Add count labels on bars
        bars.append("text")
            .attr("x", (d: Category) => xScale(d.itemCount) + 5)
            .attr("y", barHeight / 2)
            .attr("dy", "0.35em")
            .style("fill", "#333")
            .style("font-size", "11px")
            .style("pointer-events", "none")
            .text((d: Category) => d.itemCount);
    }
}
